import { Link } from 'react-router-dom';
import { PageLayout } from '@/components/poof-ui';
import NavHeader from '@/components/NavHeader';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Shield } from 'lucide-react';

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card className="mb-6" style={{ background: 'hsl(30 6% 7%)', borderColor: 'hsl(35 12% 14%)' }}>
    <CardContent className="p-8">
      <h2
        className="text-2xl font-semibold mb-4"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: 'hsl(38 65% 57%)',
          letterSpacing: '0.02em',
        }}
      >
        {title}
      </h2>
      <div className="text-sm leading-relaxed space-y-3" style={{ color: 'hsl(40 15% 72%)' }}>
        {children}
      </div>
    </CardContent>
  </Card>
);

export default function PrivacyPage() {
  return (
    <PageLayout>
      <NavHeader />
      <div className="container py-24 max-w-3xl">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm mb-10 transition-colors hover:opacity-80"
          style={{ color: 'hsl(38 65% 57%)' }}
        >
          <ArrowLeft size={15} />
          Back to SelfWorth DApp
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <Shield size={22} style={{ color: 'hsl(38 65% 57%)' }} />
            <span
              className="text-sm font-medium tracking-widest uppercase"
              style={{ color: 'hsl(35 8% 52%)' }}
            >
              Legal
            </span>
          </div>
          <h1
            className="text-5xl font-semibold mb-3"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: 'hsl(40 15% 92%)',
              lineHeight: 1.15,
            }}
          >
            Privacy Policy
          </h1>
          <p className="text-sm" style={{ color: 'hsl(35 8% 52%)' }}>
            SelfWorth DApp &nbsp;·&nbsp; Last updated: April 2026
          </p>
        </div>

        {/* Intro */}
        <p className="text-sm leading-relaxed mb-8" style={{ color: 'hsl(40 15% 72%)' }}>
          SelfWorth DApp ("we", "our", or "the App") is committed to protecting your privacy. This
          Privacy Policy explains what information we collect, how we use it, and your rights
          regarding that information. By using SelfWorth DApp, you agree to the practices described
          in this policy.
        </p>

        {/* Sections */}
        <SectionCard title="1. What Data We Collect">
          <p>
            SelfWorth DApp is a non-custodial decentralized application. We do not require account
            registration, passwords, or personal identification. The information we may collect
            includes:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Wallet addresses</strong> — your Solana
              public key, which you provide by connecting a wallet. This is a public identifier on
              the blockchain and is not considered personally identifiable information.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Transaction data</strong> — on-chain
              activity (lock amounts, durations, donations) associated with your wallet address, as
              recorded on the Solana blockchain.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Usage analytics</strong> — anonymized
              interaction data such as pages visited, features used, and session duration, collected
              to improve the App experience. This data is not linked to your wallet address.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Technical data</strong> — browser type,
              operating system, and IP address, retained for security, abuse prevention, and
              debugging purposes.
            </li>
          </ul>
          <p>We do not collect names, email addresses, phone numbers, or government-issued IDs.</p>
        </SectionCard>

        <SectionCard title="2. How We Use Your Data">
          <p>Information collected is used solely to operate and improve SelfWorth DApp:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>
              Displaying your on-chain activity, lock status, leaderboard standings, and prize pool
              eligibility within the App.
            </li>
            <li>
              Aggregating anonymized usage statistics to understand how features are used and where
              improvements can be made.
            </li>
            <li>
              Detecting and preventing fraudulent or abusive behavior that could harm other users.
            </li>
            <li>
              Complying with applicable law or responding to valid legal requests from authorities.
            </li>
          </ul>
          <p>
            We do not sell, rent, or share your data with third-party advertisers. We do not use
            your data for targeted advertising or profiling.
          </p>
        </SectionCard>

        <SectionCard title="3. Third-Party Services">
          <p>
            SelfWorth DApp interacts with external services that have their own privacy practices:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Solana Blockchain</strong> — All
              on-chain transactions are permanently recorded on the public Solana ledger. Once
              submitted, blockchain data cannot be altered or deleted. Anyone can inspect
              transactions associated with your wallet address using public block explorers.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Wallet Providers</strong> — When you
              connect a wallet (e.g., Phantom, Solflare), you interact directly with that wallet's
              interface. Their privacy policies govern how they handle your data.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Cloudflare</strong> — Our infrastructure
              is hosted on Cloudflare, which processes traffic metadata as described in Cloudflare's
              Privacy Policy.
            </li>
          </ul>
          <p>
            We encourage you to review the privacy policies of any third-party services you interact
            with through the App.
          </p>
        </SectionCard>

        <SectionCard title="4. Your Rights">
          <p>
            Depending on your jurisdiction, you may have rights regarding the limited personal data
            we hold:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Access</strong> — You may request a
              summary of any off-chain data associated with your wallet address.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Deletion</strong> — You may request
              deletion of off-chain data we hold. Note that blockchain data is immutable and cannot
              be erased.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Opt-out of analytics</strong> — You may
              disable cookies or use privacy-preserving browser settings to limit anonymized
              analytics collection.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Portability</strong> — On-chain data is
              already publicly accessible and portable via any Solana block explorer.
            </li>
          </ul>
          <p>
            To exercise your rights, contact us at the address listed below. We will respond within
            30 days.
          </p>
        </SectionCard>

        {/* Footer note */}
        <div
          className="mt-8 pt-6 text-xs"
          style={{ borderTop: '1px solid hsl(35 12% 14%)', color: 'hsl(35 8% 42%)' }}
        >
          <p>
            We may update this Privacy Policy from time to time. Continued use of SelfWorth DApp
            after changes constitutes acceptance of the revised policy. For questions, reach out via
            our community channels.
          </p>
        </div>
      </div>
    </PageLayout>
  );
}
